import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import type { Breadcrumb, BrowserOptions, Event } from '@sentry/angular';
import type { BuildEnv } from 'src/environments/build-env';

const JWT = /eyJ[\w-]*\.[\w-]+\.[\w-]+/g;
const EMAIL = /[\w.+-]+(?:@|%40)[\w-]+(?:\.[\w-]+)*\.[a-z]{2,}/gi;
// Segment bis / ? # Leerzeichen oder «: » (Angular-HTTP-Meldung «<url>: 404 …»)
const SEGMENT = String.raw`(?:(?!:\s)[^/?#\s])+`;
const SHARED_ID = new RegExp(String.raw`\/shared\/((?:dis)?connect\/)?` + SEGMENT, 'g');
const RESET_TOKEN = new RegExp(String.raw`\/token\/` + SEGMENT, 'g');
// Query-String und Hash-Fragment bis Leerzeichen/Ende; ein folgendes «: 401» (Angular-HTTP-Meldung) bleibt stehen
const QUERY_OR_HASH = /(\/[^\s?#]*)[?#]\S*?(?=:\s|\s|$)/g;
const HTTP_ERROR = /^Http failure response for (\S+?): (\d+)/;
const KEPT_BREADCRUMBS = ['navigation', 'ui.click'];

/** Entfernt Geheimnisse und personenbezogene Daten aus einem Text (URLs, Meldungen). */
export function maskText(value: string): string {
  return value
    .replace(RESET_TOKEN, '/token/[token]')
    .replace(JWT, '[jwt]')
    .replace(EMAIL, '[email]')
    .replace(SHARED_ID, '/shared/$1[id]')
    .replace(QUERY_OR_HASH, '$1');
}

/** Gruppiert HTTP-Fehler nach Status und URL-Muster (ID-artige Segmente → [id]) statt pro konkreter URL. */
function httpFingerprint(text: string): string[] | undefined {
  const m = HTTP_ERROR.exec(text);
  if (!m) return undefined;
  const path = m[1]
    .replace(/^https?:\/\/[^/]+/, '')
    .split('/')
    .map((seg) => (/^\d+$/.test(seg) || (seg.length >= 8 && /\d/.test(seg)) ? '[id]' : seg))
    .join('/');
  return ['http', m[2], path];
}

function maskBreadcrumb(b: Breadcrumb): Breadcrumb {
  const data = b.data
    ? Object.fromEntries(Object.entries(b.data).map(([k, v]) => [k, typeof v === 'string' ? maskText(v) : v]))
    : undefined;
  return { ...b, message: b.message ? maskText(b.message) : b.message, data };
}

/** Wie Backend-scrubEvent: keine Benutzer-, Request- oder Netzdaten, Texte maskiert. */
export function scrubEvent<T extends Event>(event: T): T {
  delete event.user;
  // Serialisierte Nicht-Error-Objekte (z. B. HttpErrorResponse aus Promise-Rejections) mit Bodies/Headern
  delete event.extra;
  if (event.request) {
    event.request = event.request.url ? { url: maskText(event.request.url) } : {};
  }
  if (event.message) event.message = maskText(event.message);
  event.exception?.values?.forEach((v) => {
    if (v.value) v.value = maskText(v.value);
  });
  if (event.transaction) event.transaction = maskText(event.transaction);
  const fingerprint = httpFingerprint(event.message ?? event.exception?.values?.[0]?.value ?? '');
  if (fingerprint) event.fingerprint = fingerprint;
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.filter((b) => KEPT_BREADCRUMBS.includes(b.category ?? '')).map(maskBreadcrumb);
  }
  return event;
}

/**
 * Extractor für `Sentry.createErrorHandler`: meldet HTTP-Fehler nur mit Status und URL.
 * Der Default-Extractor würde einen Text-Antwort-Body in die Meldung übernehmen.
 */
export function extractError(error: unknown, defaultExtractor: (error: unknown) => unknown): unknown {
  const original = (error as { ngOriginalError?: unknown } | null)?.ngOriginalError ?? error;
  if (original instanceof HttpErrorResponse) return original.message;
  return defaultExtractor(error);
}

export function buildSentryOptions(env: BuildEnv): BrowserOptions | null {
  const dsn = env.sentryDsn.trim();
  if (!dsn) return null;
  return {
    dsn,
    environment: env.sentryEnvironment || undefined,
    sendDefaultPii: false,
    tracePropagationTargets: [],
    // Nur Fehler: keine Sessions, keine Netz-/Console-Breadcrumbs
    integrations: (defaults) => [
      ...defaults.filter((i) => i.name !== 'BrowserSession' && i.name !== 'Breadcrumbs'),
      Sentry.breadcrumbsIntegration({ console: false, fetch: false, xhr: false, dom: true, history: true, sentry: false }),
    ],
    beforeSend: (event) => scrubEvent(event),
    beforeBreadcrumb: (b) => (KEPT_BREADCRUMBS.includes(b.category ?? '') ? maskBreadcrumb(b) : null),
  };
}

export function parseRelease(json: unknown): string | undefined {
  const v = (json as { version?: unknown } | null)?.version;
  return typeof v === 'string' && v && v !== 'unknown' ? v : undefined;
}

/** Startet GlitchTip, falls ein DSN gebaut wurde; die Release kommt nachträglich aus /version.json. */
export function initMonitoring(env: BuildEnv, fetchFn: typeof fetch = fetch.bind(window)): boolean {
  const options = buildSentryOptions(env);
  if (!options) return false;
  Sentry.init(options);
  let release: string | undefined;
  Sentry.addEventProcessor((event) => (release ? { ...event, release } : event));
  fetchFn('/version.json', { cache: 'no-store' })
    .then((r) => (r.ok ? r.json() : null))
    .then((json) => (release = parseRelease(json)))
    .catch(() => undefined);
  return true;
}
